/**
 * API Service for PolyVideo
 * 
 * Handles all API calls to the backend
 */

import axios from 'axios';
import { getApiUrl, API_ENDPOINTS } from '../config/api';

/**
 * Fetches Polymarket market information from a URL
 * @param {string} polymarketUrl - Polymarket market URL
 * @returns {Promise<Object>} Market information
 */
export const getPolymarketInfo = async (polymarketUrl) => {
  try {
    const response = await axios.post(getApiUrl(API_ENDPOINTS.POLYMARKET_INFO), {
      url: polymarketUrl
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch Polymarket data');
  }
};

/**
 * Fetches YouTube video information from a URL
 * @param {string} youtubeUrl - YouTube video URL
 * @returns {Promise<Object>} Video information
 */
export const getYoutubeInfo = async (youtubeUrl) => {
  try {
    const response = await axios.post(getApiUrl(API_ENDPOINTS.YOUTUBE_INFO), {
      url: youtubeUrl
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch YouTube data');
  }
};

/**
 * Fetches comprehensive price analytics
 * @param {Object} params - Request parameters
 * @param {string} params.tokenId - CLOB token ID
 * @param {string} params.startTime - Start time in EST format
 * @param {string} params.endTime - End time in EST format
 * @param {number} params.extremaThreshold - Threshold for extrema detection
 * @returns {Promise<Object>} Price analytics data
 */
export const getPriceAnalytics = async (params) => {
  try {
    const response = await axios.post(getApiUrl(API_ENDPOINTS.PRICES_ANALYTICS), params);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch price analytics');
  }
};

/**
 * Complete workflow: Fetch all data needed for PolyVideo
 * @param {Object} inputs - User inputs
 * @param {string} inputs.polymarketUrl - Polymarket URL
 * @param {string} inputs.youtubeUrl - YouTube URL
 * @param {string} inputs.startTime - Start time in EST
 * @param {string} inputs.endTime - End time in EST
 * @returns {Promise<Object>} Complete data for visualization
 */
export const fetchAllData = async (inputs) => {
  const { polymarketUrl, youtubeUrl, startTime, endTime } = inputs;
  
  try {
    // Step 1: Fetch Polymarket market info
    console.log('Fetching Polymarket market info...');
    const marketInfo = await getPolymarketInfo(polymarketUrl);
    
    // Step 2: Fetch YouTube video info
    console.log('Fetching YouTube video info...');
    const videoInfo = await getYoutubeInfo(youtubeUrl);
    
    // Step 3: Fetch price analytics using primary token ID
    console.log('Fetching price analytics...');
    const analytics = await getPriceAnalytics({
      tokenId: marketInfo.primaryTokenId,
      startTime,
      endTime,
    });
    
    return {
      marketInfo,
      videoInfo,
      analytics,
      inputs: {
        polymarketUrl,
        youtubeUrl,
        startTime,
        endTime
      }
    };
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error;
  }
};

const apiService = {
  getPolymarketInfo,
  getYoutubeInfo,
  getPriceAnalytics,
  fetchAllData
};

export default apiService;

